import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getLocalTrackB64 } from './localMusicBank';
import { useAudioWaveform, drawWaveform } from './useAudioWaveform';

const API = process.env.REACT_APP_API_URL || '';

function formatTime(sec) {
  if (sec == null || isNaN(sec) || !isFinite(sec)) return '--:--';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function MusicTrimmer({ filename, duration: durationProp, musicStart, onMusicStartChange, videoDuration }) {
  const audioRef    = useRef(null);
  const trackRef    = useRef(null);
  const waveRef     = useRef(null);
  const isDragging  = useRef(false);
  const barRect     = useRef(null);

  const [playing,    setPlaying]    = useState(false);
  const [localStart, setLocalStart] = useState(musicStart || 0);
  const [duration,   setDuration]   = useState(durationProp || null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (durationProp && durationProp > 0) setDuration(durationProp);
  }, [durationProp]);

  useEffect(() => { setLocalStart(musicStart || 0); }, [musicStart]);

  const streamUrl = `${API}/api/music/stream/${encodeURIComponent(filename)}`;
  const b64 = getLocalTrackB64(filename);

  // Waveform peaks (denser than MIX: 120 bars)
  const { peaks } = useAudioWaveform(
    b64 ? null : streamUrl,
    { bars: 120, b64: b64 || undefined }
  );

  const onAudioMeta = useCallback(() => {
    if (!audioRef.current) return;
    const d = audioRef.current.duration;
    if (d && isFinite(d) && d > 0) setDuration(d);
  }, []);

  const vidDur   = videoDuration || 20;
  const maxStart = duration ? Math.max(0, duration - vidDur) : 0;

  const commit = useCallback((val) => {
    const clamped = Math.max(0, Math.min(maxStart, val));
    setLocalStart(clamped);
    onMusicStartChange(clamped);
  }, [maxStart, onMusicStartChange]);

  const xToStart = useCallback((clientX) => {
    const rect = barRect.current;
    if (!rect || !duration) return 0;
    const ratio  = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const target = ratio * duration - vidDur / 2;
    return Math.max(0, Math.min(maxStart, target));
  }, [duration, vidDur, maxStart]);

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    barRect.current = trackRef.current?.getBoundingClientRect() || null;
    isDragging.current = true;
    setDragActive(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    commit(xToStart(clientX));
  }, [commit, xToStart]);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      commit(xToStart(clientX));
    };
    const onUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      setDragActive(false);
    };
    window.addEventListener('mousemove',  onMove, { passive: false });
    window.addEventListener('mouseup',    onUp);
    window.addEventListener('touchmove',  onMove, { passive: false });
    window.addEventListener('touchend',   onUp);
    return () => {
      window.removeEventListener('mousemove',  onMove);
      window.removeEventListener('mouseup',    onUp);
      window.removeEventListener('touchmove',  onMove);
      window.removeEventListener('touchend',   onUp);
    };
  }, [commit, xToStart]);

  // Waveform draw — redraws whenever peaks or trim window changes
  useEffect(() => {
    const canvas = waveRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width || canvas.offsetWidth || 300;
    const h = rect.height || canvas.offsetHeight || 40;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width  = w * dpr;
      canvas.height = h * dpr;
      canvas.getContext('2d').scale(dpr, dpr);
    }
    if (peaks) {
      drawWaveform(canvas, peaks, 0, '#F97316', 'rgba(249,115,22,0.22)');
    } else {
      // Shimmer placeholder
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, w, h);
      const n = 40;
      const gap = 1.5;
      const barW = Math.max(1, (w - gap * (n - 1)) / n);
      for (let i = 0; i < n; i++) {
        const bh = Math.max(3, Math.abs(Math.sin(i * 0.4)) * h * 0.5 + 3);
        const x = i * (barW + gap);
        const y = (h - bh) / 2;
        ctx.fillStyle = 'rgba(249,115,22,0.12)';
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(x, y, barW, bh, 2);
        } else {
          ctx.rect(x, y, barW, bh);
        }
        ctx.fill();
      }
    }
  }, [peaks, localStart, duration]);

  const handlePlay = useCallback(() => {
    if (!audioRef.current) return;
    clearTimeout(audioRef.current._stopTimer);
    audioRef.current.currentTime = localStart;
    audioRef.current.play();
    setPlaying(true);
    audioRef.current._stopTimer = setTimeout(() => {
      if (audioRef.current) audioRef.current.pause();
      setPlaying(false);
    }, vidDur * 1000);
  }, [localStart, vidDur]);

  const handleStop = useCallback(() => {
    if (!audioRef.current) return;
    clearTimeout(audioRef.current._stopTimer);
    audioRef.current.pause();
    setPlaying(false);
  }, []);

  const startPct = duration ? (localStart / duration) * 100 : 0;
  const endPct   = duration ? Math.min(100, ((localStart + vidDur) / duration) * 100) : 100;

  const dotStyle = {
    position: 'absolute', top: '50%', width: 14, height: 14, borderRadius: '50%',
    background: '#F97316', border: '2px solid #fff',
    transform: 'translate(-50%, -50%)', cursor: 'grab', zIndex: 2, touchAction: 'none'
  };

  const handleDotDrag = (e) => {
    e.preventDefault();
    const bar = trackRef.current;
    if (!bar) return;
    barRect.current = bar.getBoundingClientRect();
    isDragging.current = true;
    setDragActive(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    commit(xToStart(clientX));
  };

  return (
    <div>
      <audio
        ref={audioRef}
        src={b64 || streamUrl}
        onLoadedMetadata={onAudioMeta}
        onEnded={() => setPlaying(false)}
        preload="metadata"
      />

      {/* Waveform row — aligned on the same 1fr as the slider */}
      <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr 40px 36px', alignItems: 'center', columnGap: 8, marginBottom: 6 }}>
        <span />
        <div className="mt-wave-wrap">
          <canvas ref={waveRef} />
          <div className="mt-wave-overlay" style={{ left: 0, width: `${startPct}%` }} />
          <div className="mt-wave-overlay" style={{ right: 0, width: `${100 - endPct}%` }} />
        </div>
        <span />
        <span />
      </div>

      {/* Slider row */}
      <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr 40px 36px', alignItems: 'center', columnGap: 8, height: 36 }}>
        <span />
        <div ref={trackRef} style={{ position: 'relative', height: 14, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onMouseDown={onPointerDown} onTouchStart={onPointerDown}>
          <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 2, background: '#1a1a1a' }} />
          <div style={{ position: 'absolute', left: `${startPct}%`, width: `${endPct - startPct}%`, height: 4, borderRadius: 2, background: '#F97316' }} />
          <div style={{ ...dotStyle, left: `${startPct}%` }} onMouseDown={handleDotDrag} onTouchStart={handleDotDrag} />
          <div style={{ ...dotStyle, left: `${endPct}%`, opacity: 0.5, cursor: 'default' }} />
        </div>
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: '#F97316', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {formatTime(duration)}
        </span>
        <button onClick={playing ? handleStop : handlePlay}
          style={{ width: 32, height: 32, borderRadius: '50%', background: playing ? '#F97316' : 'white', border: '1px solid #e5e5e5', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }}>
          {playing
            ? <div style={{ width: 8, height: 8, background: '#1a1a1a', borderRadius: 1 }} />
            : <svg width="12" height="12" viewBox="0 0 24 24" fill="#1a1a1a"><path d="M8 5v14l11-7z"/></svg>}
        </button>
      </div>

      {/* Live timecode — follows the trim window */}
      <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr 40px 36px', columnGap: 8, marginTop: 4 }}>
        <span />
        <span style={{ textAlign: 'center', fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 500, color: '#F97316', fontVariantNumeric: 'tabular-nums' }}>
          {formatTime(localStart)} → {formatTime(localStart + vidDur)}
        </span>
      </div>

      {!duration && (
        <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>Loading track duration…</div>
      )}
    </div>
  );
}
